import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Group, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { Alert, Button, PageHeader } from "@xplatform/design-system";
import { IconArrowLeft, IconDeviceFloppy, IconEdit, IconEye } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Changes, FieldError } from "@models/mappingConfig";
import { useConfigSchema, useConfigVersion, usePatchDraft, useUnsavedChangesPrompt } from "@hooks/mappingConfig";
import { ApiErrorAlert, ConfigGroupTabs, ConflictModal, SchemaForm } from "@features/MappingConfig";
import {
  applyChanges,
  bodyOf,
  buildChanges,
  errorCodeOf,
  fieldErrorsOf,
  groupFields,
  groupStatus,
  hasChanges,
  isConflict,
  mappingConfigRoutes,
} from "@utils/mappingConfig";

type Body = Record<string, unknown>;

/**
 * Edits one draft with the schema-driven form. Save sends PATCH with the
 * revision the form was loaded at and only the changed fields; a 409 keeps the
 * edits in memory and, on reload, replays them onto the newer draft.
 */
export const MappingConfigEditPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { service = "", pluginId = "", versionId = "" } = useParams();

  const schema = useConfigSchema(service);
  const version = useConfigVersion(service, pluginId, versionId);
  const patch = usePatchDraft(service, pluginId);

  const [original, setOriginal] = useState<Body | null>(null);
  const [edited, setEdited] = useState<Body | null>(null);
  const [revision, setRevision] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const [tab, setTab] = useState<string | null>(null);
  const pendingRebase = useRef<Changes | null>(null);
  const groups = useMemo(() => (schema.data ? groupFields(schema.data) : []), [schema.data]);
  const activeTab = tab && groups.some((g) => g.group === tab) ? tab : groups[0]?.group;

  // (Re)load the form from the server copy; after a conflict, replay the unsaved changes onto it.
  useEffect(() => {
    if (!version.data || !schema.data) return;
    const body = bodyOf(version.data, schema.data.json_schema);
    const pending = pendingRebase.current;
    pendingRebase.current = null;
    setOriginal(body);
    setEdited(pending ? applyChanges(schema.data, body, pending) : body);
    setRevision(version.data.revision);
    setStatus(version.data.status);
    setComment((current) => current || version.data!.comment || "");
  }, [version.data, schema.data]);

  const changes = useMemo(
    () => (schema.data && original && edited ? buildChanges(schema.data, original, edited) : {}),
    [schema.data, original, edited],
  );
  const dirty = hasChanges(changes);
  const confirmLeave = useUnsavedChangesPrompt(dirty, t("mappingConfig.edit.unsavedPrompt"));
  const invalidCount = Object.keys(invalid).length;
  // Flag the tabs the user is not looking at: unsaved edits, server or client errors.
  const flags = useMemo(
    () => groupStatus(groups, edited ?? {}, original ?? undefined,
                      [...fieldErrors.map((e) => e.path), ...Object.keys(invalid)]),
    [groups, edited, original, fieldErrors, invalid],
  );

  const leaveTo = (path: string) => {
    if (confirmLeave()) navigate(path);
  };

  const save = async () => {
    if (!dirty || revision === null) return;
    setSaveError(null);
    setFieldErrors([]);
    setSaved(false);
    try {
      const response = await patch.run(versionId, { expected_revision: revision, changes, comment: comment.trim() || undefined });
      const body = bodyOf(response.version, schema.data!.json_schema);
      setOriginal(body);
      setEdited(body);
      setRevision(response.version.revision);
      setSaved(true);
    } catch (err) {
      if (isConflict(err)) {
        setConflict(errorCodeOf(err) ?? "revision_conflict");
        return;
      }
      setFieldErrors(fieldErrorsOf(err));
      setSaveError(err);
    }
  };

  const reloadAfterConflict = () => {
    pendingRebase.current = changes;
    setConflict(null);
    setSaved(false);
    version.reload();
  };

  const loading = (version.loading && !version.data) || (schema.loading && !schema.data);
  const loadError = version.error ?? schema.error;
  const notDraft = status !== null && status !== "draft";

  return (
    <Stack gap="md">
      <Group>
        <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={16} />}
                onClick={() => leaveTo(mappingConfigRoutes.plugin(service, pluginId, "drafts"))}>
          {t("mappingConfig.backToPlugin")}
        </Button>
      </Group>
      <PageHeader
        title={t("mappingConfig.edit.title")}
        description={<Text span ff="monospace" size="sm">{`${service} / ${pluginId} / ${versionId}`}</Text>}
        icon={<IconEdit size={28} />}
        actions={
          <Group gap="sm">
            {dirty && <Badge color="orange" variant="light">{t("mappingConfig.edit.unsaved")}</Badge>}
            <Button variant="light" leftSection={<IconEye size={16} />}
                    onClick={() => leaveTo(mappingConfigRoutes.review(service, pluginId, versionId))}>
              {t("mappingConfig.actions.review")}
            </Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} onClick={save} loading={patch.loading}
                    disabled={!dirty || invalidCount > 0 || notDraft}>
              {t("common.save")}
            </Button>
          </Group>
        }
      />

      {loadError && <Alert color="red" title={t("mappingConfig.loadFailed")}>{loadError}</Alert>}
      {notDraft && <Alert color="orange">{t("mappingConfig.edit.notDraft", { status })}</Alert>}
      {saved && !dirty && (
        <Alert color="green" title={t("mappingConfig.edit.saved")}>
          <Group gap="sm">
            <Text size="sm">{t("mappingConfig.edit.savedHint")}</Text>
            <Button size="xs" variant="light" onClick={() => navigate(mappingConfigRoutes.review(service, pluginId, versionId))}>
              {t("mappingConfig.actions.review")}
            </Button>
          </Group>
        </Alert>
      )}
      {invalidCount > 0 && (
        <Alert color="orange" title={t("mappingConfig.edit.invalid", { count: invalidCount })}>
          {Object.entries(invalid).map(([path, message]) => (
            <Text key={path} size="sm"><Text span ff="monospace">{path}</Text>: {message}</Text>
          ))}
        </Alert>
      )}
      {saveError !== null && <ApiErrorAlert error={saveError} title={t("mappingConfig.edit.saveFailed")} />}

      {loading ? (
        <Stack gap="md">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} h={160} radius="md" />)}</Stack>
      ) : schema.data && edited && original ? (
        <Stack gap="md">
          <TextInput
            label={t("mappingConfig.comment")}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            maxLength={2000}
            disabled={notDraft}
          />
          {activeTab && (
            <ConfigGroupTabs
              groups={groups}
              value={activeTab}
              onChange={setTab}
              changed={flags.changed}
              problems={flags.problems}
            />
          )}
          <SchemaForm
            schema={schema.data}
            value={edited}
            original={original}
            onChange={notDraft ? undefined : setEdited}
            readOnly={notDraft}
            fieldErrors={fieldErrors}
            onInvalidChange={setInvalid}
            activeGroup={activeTab}
          />
        </Stack>
      ) : null}

      <ConflictModal opened={conflict !== null} code={conflict} onClose={() => setConflict(null)} onReload={reloadAfterConflict} />
    </Stack>
  );
};
